import React from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from 'views/components';
import {Translate, translate} from 'views/module';

const ConfirmationRow = (props) => {
  return (
    <Stack direction='row' spacing={2} justifyContent='space-between'>
      <Typography sx={{fontWeight: 700}}>{props.label}</Typography>
      <Typography sx={{textAlign: 'right'}}>{props.value}</Typography>
    </Stack>
  );
};

ConfirmationRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const ConfirmationDialog = (props) => {
  const {
    channelLabel,
    dryRun,
    eligibleCount,
    entityTypeLabel,
    message,
    missingCategoriesLabel,
    onClose,
    onConfirm,
    open,
    senderEmail,
    skippedCount,
    statusLabel,
    subject,
  } = props;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm'>
      <DialogTitle onClose={onClose}>
        <Translate expression='bulkMessaging.Confirmation.Title' />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            <Translate
              expression={dryRun ?
                'bulkMessaging.Confirmation.DryDescription' :
                'bulkMessaging.Confirmation.Description'}
              substitute={{eligible: eligibleCount, skipped: skippedCount}}
            />
          </Typography>
          {
            dryRun &&
            <Alert severity='warning'>
              <Translate expression='bulkMessaging.Confirmation.DryRun' />
            </Alert>
          }
          <Stack spacing={1}>
            <ConfirmationRow
              label={translate('bulkMessaging.Confirmation.EntityType')}
              value={entityTypeLabel}
            />
            <ConfirmationRow
              label={translate('bulkMessaging.Confirmation.Status')}
              value={statusLabel}
            />
            <ConfirmationRow
              label={translate('bulkMessaging.Confirmation.Channel')}
              value={channelLabel}
            />
            {
              senderEmail &&
              <ConfirmationRow
                label={translate('bulkMessaging.Confirmation.SenderEmail')}
                value={senderEmail}
              />
            }
            {
              missingCategoriesLabel &&
              <ConfirmationRow
                label={translate('bulkMessaging.Confirmation.MissingCategories')}
                value={missingCategoriesLabel}
              />
            }
            {
              subject &&
              <ConfirmationRow
                label={translate('bulkMessaging.Confirmation.Subject')}
                value={subject}
              />
            }
          </Stack>
          <Box>
            <Typography sx={{fontWeight: 700, mb: 0.75}}>
              <Translate expression='bulkMessaging.Confirmation.Message' />
            </Typography>
            <Paper
              variant='outlined'
              sx={{p: 2, maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}
            >
              {message}
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{p: 2}}>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='bulkMessaging.Actions.Cancel' />
        </Button>
        <Button color='error' onClick={onConfirm}>
          <Translate expression={dryRun ? 'bulkMessaging.Actions.ConfirmDry' : 'bulkMessaging.Actions.ConfirmSend'} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmationDialog.propTypes = {
  channelLabel: PropTypes.string.isRequired,
  dryRun: PropTypes.bool.isRequired,
  eligibleCount: PropTypes.number.isRequired,
  entityTypeLabel: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  missingCategoriesLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  senderEmail: PropTypes.string,
  skippedCount: PropTypes.number.isRequired,
  statusLabel: PropTypes.string.isRequired,
  subject: PropTypes.string,
};

export default React.memo(ConfirmationDialog);
